import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../../../../../../../service/api';
import { AdvancedSettingsModal } from '../../../../../../../components/modals/AdvancedSettingsModal';
import { MDBIcon } from 'mdb-react-ui-kit';
import { InputFieldEdit } from '../../../../../../../components/editable/InputFieldEdit';
import { StatusIcon } from '../../../../../../../components/StatusIcon';
import { MachineConnectionButton } from '../../../../../../../components/MachineConnectionButton';
import { StatusBarActionPopup } from './StatusBarActionPopup';
import { OpMachineDTO } from '@scm/common-legacy';

export interface StatusBarProps {
    groupId: number;
    locationId: number;
    machine: OpMachineDTO;
    apiURI: string;
}
export const StatusBar = ({
    groupId,
    locationId,
    machine,
    apiURI,
}: StatusBarProps) => {
    const { t } = useTranslation();

    const canEditCredit = useMemo(
        () => machine?.permissions?.CreditEdit,
        [machine],
    );

    const StatusList = useMemo(() => {
        const badge = machine.status.length == 1;

        return (
            <div className="d-flex flex-row align-items-center ms-1">
                {machine.status.map((s) => (
                    <StatusIcon
                        key={s.id}
                        name={s.id}
                        ts={s.ts}
                        count={s.count}
                        badge={badge}
                    />
                ))}
            </div>
        );
    }, [machine.status]);

    if (!machine) return null;
    return (
        <nav
            className="navbar navbar-expand-lg navbar-light p-1 w-100 border-top"
            style={{ background: '#fc8c0342' }}
        >
            <div className="d-flex flex-row flex-fill">
                {machine.credit !== undefined && (
                    <span className="d-flex flex-row align-items-center">
                        <MDBIcon
                            icon="money-bill-alt"
                            fixed
                            color="primary"
                            className="me-1"
                        />
                        <span className="fw-bold me-1">
                            {t('machine:credit')}:
                        </span>
                        {machine.credit}
                        <span className="flex-fill d-flex justify-content-end ms-2">
                            <InputFieldEdit
                                canEdit={canEditCredit && !!machine.connection}
                                type="number"
                                value={machine.credit}
                                inputProps={{ step: machine.cUnit, min: 0 }}
                                title={t('machine:edit_credit')}
                                inputLabel={t('machine:credit')}
                                confirmText={t('update')}
                                inputTransformation={(value) =>
                                    machine.mUnit *
                                    Math.round(
                                        (value as number) / machine.mUnit,
                                    )
                                }
                                action={async (crtCredit) =>
                                    await API.PATCH(apiURI, { crtCredit })
                                }
                            />
                        </span>
                    </span>
                )}

                {StatusList}

                <div className="d-flex flex-fill"></div>
                <span
                    className="d-flex flex-row justify-content-end position-relative align-items-center text-center"
                    style={{ lineHeight: 1 }}
                >
                    {machine.serviceMode === true && (
                        <span className="badge badge-info text-dark me-1">
                            {t('machine:service_mode')}
                        </span>
                    )}
                    {machine.locked && (
                        <span title={t('machine:machine_locked')}>
                            <MDBIcon
                                icon="lock"
                                color="danger"
                                size="lg"
                                className="me-1"
                            />
                        </span>
                    )}
                    <MachineConnectionButton
                        machine={machine}
                        tooltipId="sbar-connection-tooltip"
                    />
                    <StatusBarActionPopup
                        groupId={groupId}
                        locationId={locationId}
                        machine={machine}
                        apiURI={apiURI}
                    />
                </span>
            </div>
        </nav>
    );
};
